import { Button as AntButton, Select } from "antd"
import { useEffect, useMemo, useState } from "react"
import { Button as BootstrapButton, Col, Form, Modal, Row } from "react-bootstrap"
import { AddressComponent } from "../Common/AddressAutocomplete"
import { DollarInputV2 } from "./DollarInputV2"
import * as com from "../Common"
import "../Application/FinanceRealEstate/index.css"
import "../BorrowerApplication.css"
import "./styles.css"

const FieldError = ({ message }) => (
    message ? <div className="warning-msg">{message}</div> : null
)

const PROPERTY_EDIT_FIELD_ORDER = ["address", "marketValue", "sharedWith"]

const scrollToField = (fieldKey) => {
    if (!fieldKey || typeof document === "undefined") return
    const el = document.querySelector(`[data-field="${fieldKey}"]`)
    if (el && typeof el.scrollIntoView === "function") {
        el.scrollIntoView({ behavior: "smooth", block: "center" })
    }
}

const emptyRealEstate = { properties: [] }
const REAL_ESTATE_ADDRESS_PATTERN = "^[0-9]+\\s[\\p{L}\\p{N} !:,&()'?#+\\/@-]+$"
const newKey = () => `${Date.now()}-${Math.random()}`
const purpose = (application) => `${application?.property?.purpose || ""}`.toLowerCase()
const isPurchase = (application) => purpose(application) === "purchase"
const addressText = (address = {}) => [address.street || address.address, address.unit, address.city, address.state, address.zip || address.zipcode].filter(Boolean).join(", ")
const locationKey = (item = {}) => [
    item.street || item.address || "",
    item.unit || "",
    item.city || "",
    item.state || "",
    item.zip || item.zipcode || "",
].map((part) => `${part}`.trim().toLowerCase()).join("|")
const presentIsSubjectProperty = (application, present) => {
    if (isPurchase(application)) return false
    const street = `${present?.street || present?.address || ""}`.trim()
    const subjectStreet = `${application?.property?.address || ""}`.trim()
    if (!street || !subjectStreet) return false
    return locationKey(present) === locationKey({
        street: application?.property?.address,
        unit: application?.property?.unit,
        city: application?.property?.city,
        state: application?.property?.state,
        zip: application?.property?.zipcode,
    })
}
const editorAddress = (property = {}) => [property.street || property.address, property.unit, property.city, property.state, property.zip || property.zipcode].filter(Boolean).join(", ")
const loanValue = (loan, camelField, legacyField) => loan?.[camelField] ?? loan?.[legacyField] ?? ""
const defaultMortgage = (property = {}) => ({
    key: newKey(),
    creditLoanType: "ConventionalRealEstateMortgage",
    name: "",
    accountNumber: "",
    remainingBalance: "",
    monthlyPayment: "",
    paidOffAtClosing: "",
    creditLimit: "",
    propertyAddress: property.address || "",
})
const normalizeMortgage = (loan = {}) => ({
    ...loan,
    creditLoanType: loanValue(loan, "creditLoanType", "creditloantype") || "ConventionalRealEstateMortgage",
    accountNumber: loanValue(loan, "accountNumber", "accountnumber"),
    monthlyPayment: loanValue(loan, "monthlyPayment", "monthlypayment"),
    remainingBalance: loanValue(loan, "remainingBalance", "remainingbalance"),
    paidOffAtClosing: loanValue(loan, "paidOffAtClosing", "paidoffatclosing"),
    creditLimit: loanValue(loan, "creditLimit", "creditlimit"),
})
const getStatusText = (value) => {
    const option = [
        { value: "sold", label: "Sold" },
        { value: "pending", label: "Pending sale" },
        { value: "retained", label: "Retained" },
    ].find((item) => item.value === value)
    return option ? option.label : (value || "--")
}
const getOccupancyText = (value) => {
    const option = [
        { value: "principal", label: "Primary Residence" },
        { value: "secondhome", label: "Second Home" },
        { value: "investment", label: "Investment" },
        { value: "other", label: "Other" },
    ].find((item) => item.value === value)
    return option ? option.label : (value || "--")
}
const moneyText = (value) => (value === "" || value == null ? "--" : `$${com.commaize(value)}`)
const propertyHeaderText = (property = {}) => {
    const street = property.street || property.address || ""
    const city = property.city || ""
    const state = property.state || ""
    const zip = property.zip || property.zipcode || ""
    const header = [street, city, state, zip].filter(Boolean).join(", ")
    return header || property.address || "--"
}
const withOwnedFreeAndClear = (property = {}) => {
    const loans = Array.isArray(property.loans) ? property.loans : []
    return {
        ...property,
        loans,
        ownedFreeAndClear: loans.length === 0,
    }
}
const getCreditLoanTypeText = (value) => {
    const option = [
        { value: "ConventionalRealEstateMortgage", label: "Conventional real estate mortgage" },
        { value: "FHARealEstateMortgage", label: "FHA real estate mortgage" },
        { value: "VeteransAdministrationRealEstateMortgage", label: "Veterans administration real estate mortgage" },
        { value: "USDA-RD", label: "USDA-RD" },
        { value: "Other", label: "Other" },
    ].find((item) => item.value === value)
    return option ? option.label : "N/A"
}
const paidOffAtClosingText = (value) => {
    if (value === "yes") return "Yes"
    if (value === "no") return "No"
    return ""
}
const normalize = (value) => ({
    ...emptyRealEstate,
    ...(value || {}),
            properties: Array.isArray(value?.properties) ? value.properties.map((property) => {
        const ownerPartyIds = Array.isArray(property.ownerPartyIds) ? property.ownerPartyIds.filter(Boolean) : []
        const normalized = {
            ...property,
            street: property.street || property.address || "",
            zip: property.zip || property.zipcode || "",
            loans: Array.isArray(property.loans) ? property.loans.map(normalizeMortgage) : [],
            ownership: normalizeOwnershipMode(property.ownership, ownerPartyIds),
            ownerPartyIds,
            homePartyId: property.homePartyId || "",
        }
        return withOwnedFreeAndClear({ ...normalized, address: editorAddress(normalized) })
    }) : [],
})

const normalizeOwnershipMode = (ownership, ownerPartyIds = []) => {
    const raw = `${ownership || ""}`.trim().toLowerCase()
    if (raw === "joint" || (Array.isArray(ownerPartyIds) && ownerPartyIds.length > 1)) {
        return "Joint"
    }
    return "Individual"
}

const participantDisplayName = (participant) => {
    const name = [participant?.firstName, participant?.middleName, participant?.lastName]
        .map((part) => `${part || ""}`.trim())
        .filter(Boolean)
        .join(" ")
    return name || "Applicant"
}

// rentalIncomeApplies is the rule the rental income field is shown by: section 3
// asks for rent on an investment property or a two-to-four unit one, and on
// nothing else.
const rentalIncomeApplies = (property) => (
    property?.occupancy === "investment" ||
    ["twotofour", "twotofourfamily"].includes(property?.propertyType)
)

const jointOthersSelected = (ownerPartyIds, currentPartyId) => (
    (ownerPartyIds || []).filter((id) => id && id !== currentPartyId)
)

const validatePropertyEdit = (editing, showOwnership, currentPartyId) => {
    const errors = {}
    if (!(editing.street || editing.address) || !editing.state || !editing.county || !editing.city || !editing.zip) {
        errors.address = "Please complete the property address."
    }
    if (!editing.isCurrent && !editing.marketValue) {
        errors.marketValue = "Property value is required."
    }
    if (
        showOwnership
        && normalizeOwnershipMode(editing.ownership, editing.ownerPartyIds) === "Joint"
        && jointOthersSelected(editing.ownerPartyIds, currentPartyId).length === 0
    ) {
        errors.sharedWith = "Select at least one other applicant to share with."
    }
    const firstErrorKey = PROPERTY_EDIT_FIELD_ORDER.find((key) => !!errors[key]) || null
    return { errors, firstErrorKey }
}

const defaultProperty = (currentPartyId = "") => ({
    key: newKey(), address: "", status: "", occupancy: "", propertyType: "",
    marketValue: "", creditor: "", account: "", liens: "", payment: "",
    insuranceMaintenanceTaxes: "", grossRentalIncome: "", netRentalIncome: "",
    ownedFreeAndClear: true,
    loans: [],
    ownership: "Individual",
    ownerPartyIds: currentPartyId ? [currentPartyId] : [],
    homePartyId: currentPartyId || "",
})

const PropertyEditor = ({ property, onChange, ownershipUI, fieldErrors = {}, clearFieldError }) => {
    const set = (patch) => {
        // AddressComponent reports street/state/county/city/zip in separate
        // callbacks during one place selection. Use a functional update so
        // each callback merges with the latest edit instead of overwriting the
        // previous callback's fields with a stale `property` prop.
        onChange((previous) => {
            const next = { ...(previous || property), ...patch }
            const withAddress = { ...next, address: editorAddress(next) }
            // Switching a property away from investment or 2-4 units hides the
            // rental income field but used to leave the amount in the row, so a
            // single-family principal residence kept earning rent nobody could
            // see - still exported to MISMO, still read as income. Clearing it
            // here rather than on each control covers every way the two fields
            // can change.
            const withRent = rentalIncomeApplies(withAddress)
                ? withAddress
                : { ...withAddress, grossRentalIncome: "", netRentalIncome: "" }
            return Object.prototype.hasOwnProperty.call(patch, "loans")
                ? withOwnedFreeAndClear(withRent)
                : withRent
        })
        if (clearFieldError) {
            if (["street", "unit", "state", "county", "city", "zip"].some((key) => Object.prototype.hasOwnProperty.call(patch, key))) {
                clearFieldError("address")
            }
            if (Object.prototype.hasOwnProperty.call(patch, "marketValue")) {
                clearFieldError("marketValue")
            }
            if (["ownership", "ownerPartyIds"].some((key) => Object.prototype.hasOwnProperty.call(patch, key))) {
                clearFieldError("sharedWith")
            }
        }
    }
    const loans = Array.isArray(property.loans) ? property.loans : []
    const addMortgage = () => set({ loans: [...loans, defaultMortgage(property)] })
    const updateMortgage = (index, patch) => set({
        loans: loans.map((loan, itemIndex) => (itemIndex === index ? { ...loan, ...patch } : loan)),
    })
    const clearMortgages = () => set({ loans: [] })
    const renderMortgageForm = (loan, index) => (
        <div key={loan.key || index} style={{ marginTop: 24 }}>
            <div style={{ border: "1px solid #DBDCDE", borderRadius: 8, overflow: "hidden" }}>
                <div className="property-mortgage-header">
                    <img src="/images/mortgage-loan-icon.svg" alt="bank" />
                    <span style={{ color: "#222", fontSize: 16, fontWeight: 500, marginLeft: 8 }}>Add mortgage loan</span>
                </div>
                <div style={{ padding: "16px 24px 24px" }}>
                    <Form.Row>
                        <Col md={6} xs={12} className="mb-3">
                            <Form.Label style={{ color: "#6E6E70" }}>Type</Form.Label>
                            <Select
                                style={{ width: "100%" }}
                                placeholder="Select"
                                value={loan.creditLoanType || "ConventionalRealEstateMortgage"}
                                onChange={(value) => updateMortgage(index, { creditLoanType: value })}
                            >
                                <Select.Option value="ConventionalRealEstateMortgage">Conventional</Select.Option>
                                <Select.Option value="FHARealEstateMortgage">FHA</Select.Option>
                                <Select.Option value="VeteransAdministrationRealEstateMortgage">VA</Select.Option>
                                <Select.Option value="USDA-RD">USDA-RD</Select.Option>
                                <Select.Option value="Other">Other</Select.Option>
                            </Select>
                        </Col>
                        <Col md={6} xs={12} className="mb-3">
                            <Form.Label style={{ color: "#6E6E70" }}>Lender</Form.Label>
                            <Form.Control className="liabilities-form-control-sm" style={{ height: 44, borderRadius: 4 }} value={loan.name || ""} onChange={(e) => updateMortgage(index, { name: e.target.value })} />
                        </Col>
                    </Form.Row>
                    <Form.Row>
                        <Col md={6} xs={12} className="mb-3">
                            <Form.Label style={{ color: "#6E6E70" }}>Account number</Form.Label>
                            <Form.Control className="liabilities-form-control-sm" placeholder="Account number" style={{ height: 44, borderRadius: 4 }} value={loan.accountNumber || ""} onChange={(e) => updateMortgage(index, { accountNumber: e.target.value })} />
                        </Col>
                        <Col md={6} xs={12} className="mb-3">
                            <Form.Label style={{ color: "#6E6E70" }}>Mo. payment</Form.Label>
                            <DollarInputV2 placeholder="---" value={loan.monthlyPayment || ""} onChange={(value) => updateMortgage(index, { monthlyPayment: `${value || ""}` })} />
                        </Col>
                    </Form.Row>
                    <Form.Row>
                        <Col md={6} xs={12} className="mb-3">
                            <Form.Label style={{ color: "#6E6E70" }}>Unpaid balance</Form.Label>
                            <DollarInputV2 placeholder="---" value={loan.remainingBalance || ""} onChange={(value) => updateMortgage(index, { remainingBalance: `${value || ""}` })} />
                        </Col>
                        <Col md={6} xs={12} className="mb-3">
                            <Form.Label style={{ color: "#6E6E70" }}>Paid off at/before closing?</Form.Label>
                            <Select
                                style={{ width: "100%" }}
                                placeholder="Select"
                                value={loan.paidOffAtClosing || undefined}
                                onChange={(value) => updateMortgage(index, { paidOffAtClosing: value })}
                            >
                                <Select.Option value="yes">Yes</Select.Option>
                                <Select.Option value="no">No</Select.Option>
                            </Select>
                        </Col>
                    </Form.Row>
                    <Form.Row>
                        <Col md={6} xs={12}>
                            <Form.Label style={{ color: "#6E6E70" }}>Credit limit (optional)</Form.Label>
                            <DollarInputV2 placeholder="---" value={loan.creditLimit || ""} onChange={(value) => updateMortgage(index, { creditLimit: `${value || ""}` })} />
                        </Col>
                    </Form.Row>
                    <AntButton type="link" danger onClick={() => set({ loans: loans.filter((_, itemIndex) => itemIndex !== index) })}>Remove</AntButton>
                </div>
            </div>
        </div>
    )
    return (
        <Form>
            <div data-field="address">
                <AddressComponent
                    id={`realestate-address-${property.key || "new"}`}
                    label="*Address"
                    address={property.street ?? property.address ?? ""}
                    unit={property.unit}
                    state={property.state}
                    county={property.county}
                    city={property.city}
                    zip={property.zip}
                    placeholder="Street address"
                    onAddressLineChange={(value) => set({ street: value })}
                    onUnitChange={(value) => set({ unit: value })}
                    onStateChange={(value) => set({ state: value })}
                    onCountyChange={(value) => set({ county: value })}
                    onCityChange={(value) => set({ city: value })}
                    onZipChange={(value) => set({ zip: value })}
                    pattern={REAL_ESTATE_ADDRESS_PATTERN}
                />
                <FieldError message={fieldErrors.address} />
            </div>
            {ownershipUI?.show ? (
                <Form.Row>
                    <Col md={6} className="mb-3 mt-3">
                        <Form.Group className="mb-3">
                            <Form.Label className="text-left text-nowrap">Ownership</Form.Label>
                            <Select
                                className="w-100 real-estate-share-select"
                                style={{ height: 44 }}
                                disabled={!ownershipUI.canEdit}
                                value={normalizeOwnershipMode(property.ownership, property.ownerPartyIds)}
                                options={[
                                    { value: "Individual", label: "Individual" },
                                    { value: "Joint", label: "Joint" },
                                ]}
                                onChange={(value) => {
                                    if (value === "Joint") {
                                        const others = jointOthersSelected(property.ownerPartyIds, ownershipUI.currentPartyId)
                                        const defaultOthers = ownershipUI.otherParticipants.length === 1
                                            ? [ownershipUI.otherParticipants[0].partyId].filter(Boolean)
                                            : others
                                        set({
                                            ownership: "Joint",
                                            ownerPartyIds: [ownershipUI.currentPartyId, ...defaultOthers].filter(Boolean),
                                        })
                                        return
                                    }
                                    set({
                                        ownership: "Individual",
                                        ownerPartyIds: ownershipUI.currentPartyId ? [ownershipUI.currentPartyId] : [],
                                    })
                                }}
                            />
                        </Form.Group>
                    </Col>
                    {normalizeOwnershipMode(property.ownership, property.ownerPartyIds) === "Joint" ? (
                        <Col md={6} className="mb-3 mt-3" data-field="sharedWith">
                            <Form.Group className="mb-3">
                                <Form.Label className="text-left text-nowrap">*Shared with</Form.Label>
                                <Select
                                    mode="multiple"
                                    allowClear={ownershipUI.canEdit}
                                    disabled={!ownershipUI.canEdit}
                                    showSearch
                                    optionFilterProp="label"
                                    placeholder="Select applicants"
                                    className="w-100 real-estate-share-select"
                                    style={{ width: "100%", minHeight: 44 }}
                                    status={fieldErrors.sharedWith ? "error" : undefined}
                                    value={jointOthersSelected(property.ownerPartyIds, ownershipUI.currentPartyId)}
                                    options={(ownershipUI.otherParticipants || []).map((person) => ({
                                        value: person.partyId,
                                        label: participantDisplayName(person),
                                    }))}
                                    onChange={(ids) => set({
                                        ownership: "Joint",
                                        ownerPartyIds: [ownershipUI.currentPartyId, ...(ids || [])].filter(Boolean),
                                    })}
                                />
                                <FieldError message={fieldErrors.sharedWith} />
                            </Form.Group>
                        </Col>
                    ) : null}
                </Form.Row>
            ) : null}
            <div className="property-section-title" style={{ marginTop: 32, marginBottom: 20 }}>Property Info</div>
            <Form.Row>
                <Col md={6} className="mb-3" data-field="marketValue">
                    <Form.Group className="mb-3">
                        <Form.Label>{!property.isCurrent ? "*Property value" : "Property value"}</Form.Label>
                        <DollarInputV2
                            value={property.marketValue || ""}
                            status={fieldErrors.marketValue ? "error" : undefined}
                            onChange={(value) => set({ marketValue: `${value || ""}` })}
                        />
                        <FieldError message={fieldErrors.marketValue} />
                    </Form.Group>
                </Col>
                <Col md={6} className="mb-3">
                    <Form.Group className="mb-3">
                        <Form.Label>Status</Form.Label>
                        <Select
                            className="w-100"
                            style={{ height: 44 }}
                            value={property.status || undefined}
                            options={[
                                { value: "sold", label: "Sold" },
                                { value: "pending", label: "Pending sale" },
                                { value: "retained", label: "Retained" },
                            ]}
                            onChange={(value) => set({ status: value })}
                        />
                    </Form.Group>
                </Col>
            </Form.Row>
            <Form.Row>
                <Col md={6} className="mb-3">
                    <Form.Group className="mb-3">
                        <Form.Label>Property intended occupancy</Form.Label>
                        <Select
                            className="w-100"
                            style={{ height: 44 }}
                            value={property.occupancy || undefined}
                            options={[
                                { value: "principal", label: "Primary Residence" },
                                { value: "secondhome", label: "Second Home" },
                                { value: "investment", label: "Investment" },
                                { value: "other", label: "Other" },
                            ]}
                            onChange={(value) => set({ occupancy: value })}
                        />
                    </Form.Group>
                </Col>
                <Col md={6} className="mb-3">
                    <Form.Group className="mb-3">
                        <Form.Label>Monthly Insurance,Taxes, Association Dues, etc</Form.Label>
                        <DollarInputV2 value={property.insuranceMaintenanceTaxes || ""} onChange={(value) => set({ insuranceMaintenanceTaxes: `${value || ""}` })} />
                    </Form.Group>
                </Col>
            </Form.Row>
            {!property.isSubject && (
                <Form.Row>
                    <Col xs={12}>
                        <Form.Check type="checkbox" className="formLabel" label="This is a 2-4 unit property" checked={property.propertyType === "twotofour"} onChange={(e) => set({ propertyType: e.target.checked ? "twotofour" : "" })} />
                    </Col>
                </Form.Row>
            )}
            {rentalIncomeApplies(property) && (
                <Form.Row>
                    <Col md={6} className="mb-3">
                        <Form.Group className="mb-3">
                            <Form.Label>Expected monthly rental income</Form.Label>
                            <DollarInputV2 value={property.grossRentalIncome || ""} onChange={(value) => set({ grossRentalIncome: `${value || ""}` })} />
                        </Form.Group>
                    </Col>
                </Form.Row>
            )}
            <hr style={{ margin: "24px 0", border: 0, borderTop: "1px solid #DBDCDE" }} />
            <Form.Label className="property-section-title">Mortgage on this property</Form.Label>
            <Form.Check
                type="checkbox"
                className="mortgage-checkbox"
                label="I don't have a mortgage on this property."
                checked={loans.length === 0}
                onChange={(e) => (e.target.checked ? clearMortgages() : addMortgage())}
            />
            {loans.map(renderMortgageForm)}
            {loans.length > 0 && (
                <BootstrapButton variant="link" className="p-0 add-additional-mortgage-btn" onClick={addMortgage}>
                    Add additional mortgage to this property
                </BootstrapButton>
            )}
        </Form>
    )
}

const RealEstateV2 = ({ application, participant, realEstate: initialRealEstate, saving, onSave, onPersist, onBack }) => {
    const [realEstate, setRealEstate] = useState(emptyRealEstate)
    const [editing, setEditing] = useState(null)
    const [modalFieldErrors, setModalFieldErrors] = useState({})
    const [subjectProperty, setSubjectProperty] = useState(null)
    const [currentResidence, setCurrentResidence] = useState(null)
    const currentPartyId = participant?.partyId || ""
    const primaryPartyId = application?.primaryPartyId || ""
    const showOwnership = (application?.participantOrder || []).length > 1

    const clearModalFieldError = (field) => {
        setModalFieldErrors((prev) => {
            if (!prev[field]) return prev
            const next = { ...prev }
            delete next[field]
            return next
        })
    }
    const otherParticipants = useMemo(() => (
        (application?.participantOrder || [])
            .filter((id) => id && id !== currentPartyId)
            .map((id) => application?.participantsById?.[id])
            .filter(Boolean)
    ), [application, currentPartyId])

    useEffect(() => setRealEstate(normalize(initialRealEstate)), [initialRealEstate])
    const sharedFromOthers = useMemo(() => {
        const byId = application?.finance?.realEstateByPartyId || {}
        const extras = []
        for (const id of application?.participantOrder || []) {
            if (!id || id === currentPartyId) continue
            for (const item of byId[id]?.properties || []) {
                const owners = Array.isArray(item.ownerPartyIds) ? item.ownerPartyIds : []
                if (!owners.includes(currentPartyId)) continue
                extras.push({
                    ...item,
                    homePartyId: item.homePartyId || id,
                })
            }
        }
        return extras
    }, [application, currentPartyId])

    const subjectOwnerIds = Array.isArray(application?.property?.ownerPartyIds)
        ? application.property.ownerPartyIds.filter(Boolean)
        : []
    const subjectHomePartyId = application?.property?.homePartyId || primaryPartyId
    const canEditSubject = currentPartyId !== "" && currentPartyId === subjectHomePartyId
    const showSubject = !isPurchase(application) && (
        subjectOwnerIds.length > 0
            ? subjectOwnerIds.includes(currentPartyId)
            : currentPartyId === primaryPartyId
    )

    const subject = useMemo(() => withOwnedFreeAndClear({
        key: "subject-property",
        street: application?.property?.address || "",
        unit: application?.property?.unit || "",
        city: application?.property?.city || "",
        county: application?.property?.county || "",
        state: application?.property?.state || "",
        zip: application?.property?.zipcode || "",
        address: addressText(application?.property),
        marketValue: application?.property?.appraisal || application?.property?.salesprice || "",
        status: application?.property?.status || "",
        occupancy: application?.property?.occupancy || "",
        propertyType: application?.property?.propertytype || "",
        insuranceMaintenanceTaxes: application?.property?.insurancemaintenancetaxes || "",
        grossRentalIncome: application?.property?.grossrentalincome || "",
        netRentalIncome: application?.property?.netrentalincome || "",
        loans: Array.isArray(application?.property?.loans) ? application.property.loans.map(normalizeMortgage) : [],
        isSubject: true,
        ownership: normalizeOwnershipMode(application?.property?.ownership, application?.property?.ownerPartyIds),
        ownerPartyIds: Array.isArray(application?.property?.ownerPartyIds) && application.property.ownerPartyIds.length
            ? application.property.ownerPartyIds.filter(Boolean)
            : (primaryPartyId ? [primaryPartyId] : []),
        homePartyId: application?.property?.homePartyId || primaryPartyId,
    }), [application, primaryPartyId])
    useEffect(() => setSubjectProperty(subject), [subject])
    useEffect(() => {
        const currentAddress = addressText(participant?.presentAddress)
        const currentKey = currentPartyId ? `current-residence:${currentPartyId}` : "current-residence"
        const presentKey = locationKey(participant?.presentAddress)
        const savedProperty = normalize(initialRealEstate).properties.find((item) => (
            item.key === currentKey
            || item.key === "current-residence"
            || (presentKey && locationKey(item) === presentKey)
            || item.address === currentAddress
        ))
        if (participant?.residenceOwnership !== "own") {
            setCurrentResidence(null)
            return
        }
        setCurrentResidence(withOwnedFreeAndClear({
            ...savedProperty,
            key: savedProperty?.key || currentKey,
            ...participant.presentAddress,
            address: currentAddress,
            loans: savedProperty?.loans || [],
            isCurrent: true,
            homePartyId: savedProperty?.homePartyId || currentPartyId,
            ownerPartyIds: savedProperty?.ownerPartyIds?.length
                ? savedProperty.ownerPartyIds
                : (currentPartyId ? [currentPartyId] : []),
            ownership: savedProperty?.ownership || "Individual",
        }))
    }, [participant, initialRealEstate, currentPartyId])
    const includeCurrentResidence = Boolean(currentResidence) && (
        isPurchase(application)
        || (
            participant?.isPrimary
                ? application?.financingsameproperty !== true
                : !presentIsSubjectProperty(application, participant?.presentAddress)
        )
    )
    const ownAdditional = currentResidence
        ? realEstate.properties.filter((item) => (
            item.key !== currentResidence.key
            && locationKey(item) !== locationKey(currentResidence)
            && item.address !== currentResidence.address
        ))
        : realEstate.properties
    const sharedAdditional = sharedFromOthers.filter((item) => (
        !currentResidence
        || (
            item.key !== currentResidence.key
            && locationKey(item) !== locationKey(currentResidence)
            && item.address !== currentResidence.address
        )
    ))
    const additionalProperties = [...ownAdditional, ...sharedAdditional]
    const properties = isPurchase(application)
        ? [currentResidence, ...additionalProperties].filter(Boolean)
        : [showSubject ? subjectProperty : null, includeCurrentResidence ? currentResidence : null, ...additionalProperties].filter(Boolean)
    const isSharedViewer = (property) => {
        const homeId = property?.homePartyId || (property?.isSubject ? subjectHomePartyId : currentPartyId)
        const owners = property?.ownerPartyIds || []
        return Boolean(currentPartyId) && owners.includes(currentPartyId) && homeId !== currentPartyId
    }
    const ownershipUIFor = (property) => ({
        show: showOwnership,
        canEdit: showOwnership && !isSharedViewer(property),
        currentPartyId,
        otherParticipants,
    })
    const buildPersistPayload = (nextRealEstate, nextCurrentResidence, nextSubjectProperty) => {
        const nextIncludeCurrentResidence = Boolean(nextCurrentResidence) && (
            isPurchase(application)
            || (
                participant?.isPrimary
                    ? application?.financingsameproperty !== true
                    : !presentIsSubjectProperty(application, participant?.presentAddress)
            )
        )
        const nextOwnAdditional = nextCurrentResidence
            ? nextRealEstate.properties.filter((item) => (
                item.key !== nextCurrentResidence.key
                && locationKey(item) !== locationKey(nextCurrentResidence)
                && item.address !== nextCurrentResidence.address
            ))
            : nextRealEstate.properties
        const nextSavedHomeProperties = (
            nextIncludeCurrentResidence && nextCurrentResidence
                ? [nextCurrentResidence, ...nextOwnAdditional]
                : (nextCurrentResidence ? nextOwnAdditional : nextRealEstate.properties)
        )
            .filter((item) => !isSharedViewer(item))
            .map(withOwnedFreeAndClear)

        return {
            realEstate: {
                ...nextRealEstate,
                properties: nextSavedHomeProperties,
            },
            residencePayload: nextCurrentResidence ? {
                presentAddress: {
                    ...participant?.presentAddress,
                    street: nextCurrentResidence.street || "",
                    unit: nextCurrentResidence.unit || "",
                    city: nextCurrentResidence.city || "",
                    county: nextCurrentResidence.county || "",
                    state: nextCurrentResidence.state || "",
                    zip: nextCurrentResidence.zip || "",
                },
                mailingAddress: participant?.mailingAddress || {},
                previousAddresses: participant?.previousAddresses || [],
                sameMailingAddress: participant?.sameMailingAddress,
                residenceOwnership: participant?.residenceOwnership,
                monthlyRent: participant?.monthlyRent || "",
            } : null,
            propertyPayload: !isPurchase(application) && canEditSubject && nextSubjectProperty ? {
                property: {
                    ...application?.property,
                    address: nextSubjectProperty.street || "",
                    unit: nextSubjectProperty.unit || "",
                    city: nextSubjectProperty.city || "",
                    county: nextSubjectProperty.county || "",
                    state: nextSubjectProperty.state || "",
                    zipcode: nextSubjectProperty.zip || "",
                    status: nextSubjectProperty.status || "",
                    occupancy: nextSubjectProperty.occupancy || "",
                    propertytype: nextSubjectProperty.propertyType || application?.property?.propertytype || "",
                    appraisal: nextSubjectProperty.marketValue || "",
                    insurancemaintenancetaxes: nextSubjectProperty.insuranceMaintenanceTaxes || "",
                    grossrentalincome: nextSubjectProperty.grossRentalIncome || "",
                    netrentalincome: nextSubjectProperty.netRentalIncome || "",
                    loans: nextSubjectProperty.loans || [],
                    isManufacturedHome: application?.property?.isManufacturedHome
                        || application?.property?.propertytype === "mobilehome"
                        || nextSubjectProperty.propertyType === "mobilehome",
                    mobilehomeinfo: application?.property?.mobilehomeinfo || {},
                    ownership: nextSubjectProperty.ownership || "Individual",
                    ownerPartyIds: nextSubjectProperty.ownerPartyIds || (primaryPartyId ? [primaryPartyId] : []),
                    homePartyId: subjectHomePartyId,
                },
                loanbalance: application?.loanbalance || "",
                financingsameproperty: application?.financingsameproperty ?? null,
                firsttimebuyer: application?.firsttimebuyer,
                selection: application?.selection || {},
            } : null,
        }
    }
    const openNew = () => {
        setModalFieldErrors({})
        setEditing({ ...defaultProperty(currentPartyId), isNew: true })
    }
    const saveEdit = async () => {
        if (!editing) return
        // English field errors only — no antd toast / browser-locale messages.
        const { errors, firstErrorKey } = validatePropertyEdit(editing, showOwnership, currentPartyId)
        if (firstErrorKey) {
            setModalFieldErrors(errors)
            setTimeout(() => scrollToField(firstErrorKey), 50)
            return
        }
        setModalFieldErrors({})
        const saved = withOwnedFreeAndClear(editing)
        const nextSubjectProperty = saved.isSubject ? saved : subjectProperty
        const nextCurrentResidence = saved.isCurrent ? { ...saved, isCurrent: true } : currentResidence
        let nextRealEstate = realEstate
        if (saved.isNew) {
            nextRealEstate = { ...realEstate, properties: [...realEstate.properties, { ...saved, isNew: undefined }] }
        } else if (!saved.isSubject && !saved.isCurrent) {
            nextRealEstate = { ...realEstate, properties: realEstate.properties.map((item) => item.key === saved.key ? saved : item) }
        }
        if (onPersist) {
            const persisted = await onPersist(buildPersistPayload(nextRealEstate, nextCurrentResidence, nextSubjectProperty))
            if (persisted === false) {
                return
            }
        }
        setSubjectProperty(nextSubjectProperty)
        setCurrentResidence(nextCurrentResidence)
        setRealEstate(nextRealEstate)
        setEditing(null)
    }
    const remove = (property) => setRealEstate((prev) => ({ ...prev, properties: prev.properties.filter((item) => item.key !== property.key) }))
    const openEdit = (property, withMortgage = false) => {
        const next = { ...property }
        if (withMortgage && (!next.loans || next.loans.length === 0)) {
            next.loans = [defaultMortgage(property)]
            Object.assign(next, withOwnedFreeAndClear(next))
        }
        setModalFieldErrors({})
        setEditing(next)
    }
    const renderMortgageSummary = (loans = []) => loans.length > 0 && (
        <>
            <div className="property-section-divider"></div>
            <div>
                <div className="property-section-title">Mortgage loans on this property</div>
                <div>
                    {loans.map((loan, index) => {
                        const normalizedLoan = normalizeMortgage(loan)
                        return (
                            <div key={normalizedLoan.key || index} className="mortgage-loan-card">
                                <div className="mortgage-loan-title">{getCreditLoanTypeText(normalizedLoan.creditLoanType)}</div>
                                <Row className="mortgage-loan-info">
                                    <Col className="mortgage-info-item" md={6}>
                                        <div className="mortgage-info-label">Lender</div>
                                        <div className="mortgage-info-value">{normalizedLoan.name || ""}</div>
                                    </Col>
                                    <Col className="mortgage-info-item" md={6}>
                                        <div className="mortgage-info-label">Account number</div>
                                        <div className="mortgage-info-value">{normalizedLoan.accountNumber || ""}</div>
                                    </Col>
                                    <Col className="mortgage-info-item" md={6}>
                                        <div className="mortgage-info-label">Unpaid balance</div>
                                        <div className="mortgage-info-value">{normalizedLoan.remainingBalance ? `$ ${com.commaize(normalizedLoan.remainingBalance)}` : ""}</div>
                                    </Col>
                                    <Col className="mortgage-info-item" md={6}>
                                        <div className="mortgage-info-label">Mo. payment</div>
                                        <div className="mortgage-info-value">{normalizedLoan.monthlyPayment ? `$${com.commaize(normalizedLoan.monthlyPayment)}` : ""}</div>
                                    </Col>
                                    <Col className="mortgage-info-item" md={6}>
                                        <div className="mortgage-info-label">Paid off at/before closing</div>
                                        <div className="mortgage-info-value">{paidOffAtClosingText(normalizedLoan.paidOffAtClosing)}</div>
                                    </Col>
                                    <Col className="mortgage-info-item" md={6}>
                                        <div className="mortgage-info-label">Credit limit (optional)</div>
                                        <div className="mortgage-info-value">{normalizedLoan.creditLimit ? `$${com.commaize(normalizedLoan.creditLimit)}` : ""}</div>
                                    </Col>
                                </Row>
                            </div>
                        )
                    })}
                </div>
            </div>
        </>
    )
    const handleNext = () => onSave(buildPersistPayload(realEstate, currentResidence, subjectProperty))
    return (
        <>
            <div className="text-left income-application newInterviewPaneProperty application-v2-form-body finance-v2-body">
                <div className="page-header page-header-row"><span className="page-header-primary">Finance</span><span className="page-header-separator">/</span><span className="page-header-secondary">Real estate</span></div>
                <div className="all-tips mt-4">Tell us about any properties you own:</div>
                <div className="finance-v2-section-note">
                    Include any property you&apos;re obligated on a mortgage for, even if you&apos;re not on the title.
                </div>
                <div className="finance-v2-section-header"><div className="finance-v2-section-title">Properties</div><AntButton type="link" className="finance-v2-add-btn" onClick={openNew}>+ Add property</AntButton></div>
                {properties.map((property, index) => (
                    <div key={property.key || index} className="employment-list mt-2 text-left">
                        <div className="employment-item">
                            <div
                                style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    width: "100%",
                                    height: 52,
                                    fontSize: 16,
                                    backgroundColor: "#F5F5F7",
                                    borderRadius: "8px 8px 0px 0px",
                                    padding: "1px 13px",
                                }}
                            >
                                <div
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        maxWidth: "calc(100% - 94px)",
                                        gap: "12px",
                                    }}
                                >
                                    <img src="/images/property-icon.svg" alt="property" />
                                    <div
                                        style={{
                                            flex: 1,
                                            whiteSpace: "nowrap",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                        }}
                                    >
                                        {propertyHeaderText(property)}
                                    </div>
                                </div>
                                <div className="real-estate-tools">
                                    {!property.isSubject && !property.isCurrent && !isSharedViewer(property) && (
                                        <div onClick={() => remove(property)}>Delete</div>
                                    )}
                                    {!isSharedViewer(property) && (
                                        <div onClick={() => openEdit(property)}>Edit</div>
                                    )}
                                </div>
                            </div>
                            <div className="property-section">
                                <div>
                                    <div className="property-section-title">
                                        <span>Property info</span>
                                        {property.isCurrent && (
                                            <span className="real-estate-subject-property">Current residence</span>
                                        )}
                                        {property.isSubject && (
                                            <span className="real-estate-subject-property">Subject property</span>
                                        )}
                                        {isSharedViewer(property) && (
                                            <span className="real-estate-subject-property">Shared with you</span>
                                        )}
                                    </div>
                                    <div>
                                        <div className="property-info-row">
                                            <div className="property-info-label">Property value</div>
                                            <div className="property-info-value">{moneyText(property.marketValue)}</div>
                                        </div>
                                        <div className="property-info-row">
                                            <div className="property-info-label">Status</div>
                                            <div className="property-info-value">{getStatusText(property.status)}</div>
                                        </div>
                                        <div className="property-info-row">
                                            <div className="property-info-label">Intended occupancy</div>
                                            <div className="property-info-value">{getOccupancyText(property.occupancy)}</div>
                                        </div>
                                        <div className="property-info-row">
                                            <div className="property-info-label">Monthly Insurance,Taxes, Association Dues, etc</div>
                                            <div className="property-info-value">{moneyText(property.insuranceMaintenanceTaxes)}</div>
                                        </div>
                                    </div>
                                </div>
                                {(!property.loans || property.loans.length === 0) && !isSharedViewer(property) && (
                                    <div className="noMortgage">
                                        <div className="noMortgage-text">No mortgage linked to this property yet. </div>
                                        <div className="noMortgage-btn" onClick={() => openEdit(property, true)}>Add a mortgage</div>
                                    </div>
                                )}
                                {renderMortgageSummary(property.loans || [])}
                            </div>
                        </div>
                    </div>
                ))}
            </div>
            <Modal className="employment-modal" size="lg" show={!!editing} onHide={() => { setEditing(null); setModalFieldErrors({}) }}>
                <Modal.Header closeButton><Modal.Title>{editing?.isSubject || editing?.isCurrent ? "Property you own" : "Additional property you own"}</Modal.Title></Modal.Header>
                <Modal.Body style={{ padding: "16px 40px 14px", maxHeight: "80vh", overflow: "auto" }}>
                    {editing && (
                        <PropertyEditor
                            property={editing}
                            onChange={setEditing}
                            ownershipUI={ownershipUIFor(editing)}
                            fieldErrors={modalFieldErrors}
                            clearFieldError={clearModalFieldError}
                        />
                    )}
                </Modal.Body>
                <Modal.Footer><BootstrapButton variant="zeitro-primary" onClick={saveEdit} disabled={saving}>Save</BootstrapButton></Modal.Footer>
            </Modal>
            <Row className="text-left application-bottom-bar"><div className="d-flex align-items-center"><BootstrapButton type="button" variant="zeitro-primary" disabled={saving} className="operate-btn back mr-3" onClick={onBack}>Back</BootstrapButton><BootstrapButton type="button" variant="zeitro-primary" disabled={saving} className="operate-btn btn" onClick={handleNext}>Next</BootstrapButton></div></Row>
        </>
    )
}

export default RealEstateV2
